import { useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import { t } from "@/i18n";

// ─────────────────────────────────────────────────────────────
// VISITE GUIDÉE
//
// Chaque étape cible un élément réel de la page via un attribut
// data-tour="..." posé sur ce bouton — pas de coordonnées codées en
// dur, donc ça survit à un changement de mise en page.
//
// Le flou est obtenu avec QUATRE bandes (haut/bas/gauche/droite)
// recalculées autour de l'élément ciblé, chacune avec son propre
// backdrop-blur — c'est ce qui permet un vrai flou avec un trou
// dedans, ce qu'un simple overlay plein écran ne peut pas faire.
// ─────────────────────────────────────────────────────────────

export const TOUR_STEPS = [
  {
    selector: '[data-tour="island"]',
    titleKey: "tutorial.bienvenue_sur_ton_oasis",
    textKey: "tutorial.glisse_pour_te_deplacer",
  },
  {
    selector: '[data-tour="tab-boutique"]',
    titleKey: "tutorial.boutique_habitants",
    textKey: "tutorial.achete_des_decors",
  },
  {
    selector: '[data-tour="tab-sac"]',
    titleKey: "tutorial.ton_inventaire",
    textKey: "tutorial.tout_ce_que_tu_possedes",
  },
  {
    selector: '[data-tour="edit-btn"]',
    titleKey: "tutorial.mode_amenagement",
    textKey: "tutorial.touche_ce_bouton",
  },
];

export default function Tutorial({ active, onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const currentStep = TOUR_STEPS[step];
  const current = {
    title: t(currentStep.titleKey),
    text: t(currentStep.textKey),
  };

  useEffect(() => {
    if (!active) return;
    let raf,
      cancelled = false;

    const measure = () => {
      if (cancelled) return;
      if (!currentStep.selector) {
        setRect(null);
        return;
      }
      const el = document.querySelector(currentStep.selector);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    const el = currentStep.selector
      ? document.querySelector(currentStep.selector)
      : null;
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const tId = setTimeout(measure, 380);
      raf = requestAnimationFrame(function loop() {
        measure();
        raf = requestAnimationFrame(loop);
      });
      return () => {
        cancelled = true;
        clearTimeout(tId);
        cancelAnimationFrame(raf);
      };
    }
    setRect(null);
    return () => {
      cancelled = true;
    };
  }, [active, step]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!active) return null;

  const next = () =>
    step < TOUR_STEPS.length - 1 ? setStep((s) => s + 1) : finish();
  const prev = () => setStep((s) => Math.max(0, s - 1));
  const finish = () => {
    setStep(0);
    onClose();
  };

  const pad = 10;
  const box = rect
    ? {
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      }
    : null;

  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const below = box && box.top + box.height + 220 < vh;
  const tooltipStyle = !box
    ? {
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }
    : {
        position: "fixed",
        top: below
          ? box.top + box.height + 14
          : Math.max(14, box.top - 14 - 200),
        left: Math.min(Math.max(14, box.left), vw - 320),
        width: 300,
      };

  return (
    <div className="fixed inset-0 z-[300]" aria-live="polite">
      {box ? (
        <>
          <Blur style={{ top: 0, left: 0, right: 0, height: box.top }} />
          <Blur
            style={{ top: box.top + box.height, left: 0, right: 0, bottom: 0 }}
          />
          <Blur
            style={{
              top: box.top,
              left: 0,
              width: box.left,
              height: box.height,
            }}
          />
          <Blur
            style={{
              top: box.top,
              left: box.left + box.width,
              right: 0,
              height: box.height,
            }}
          />
          <div
            className="fixed rounded-2xl ring-4 ring-amber-400 pointer-events-none transition-all duration-300"
            style={{
              top: box.top,
              left: box.left,
              width: box.width,
              height: box.height,
              boxShadow:
                "0 0 0 4px rgba(251,191,36,.35), 0 0 30px rgba(251,191,36,.5)",
            }}
          />
        </>
      ) : (
        <div
          className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          onClick={finish}
        />
      )}

      <div style={tooltipStyle} className={box ? "" : "px-4"}>
        <div
          className="w-full max-w-[300px] rounded-2xl bg-card border shadow-2xl p-4"
          style={{ animation: "tourPop .25s cubic-bezier(.2,1.3,.4,1)" }}
        >
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-head font-bold text-base leading-tight">
              {current.title}
            </h3>
            <button
              type="button"
              onClick={finish}
              className="shrink-0 rounded-full p-1 hover:bg-muted -mt-1 -mr-1"
            >
              <X size={16} />
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-1.5 leading-snug">
            {current.text}
          </p>

          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-1">
              {TOUR_STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`w-1.5 h-1.5 rounded-full ${i === step ? "bg-primary" : "bg-muted-foreground/30"}`}
                />
              ))}
            </div>
            <div className="flex gap-1.5">
              {step > 0 && (
                <button
                  type="button"
                  onClick={prev}
                  className="w-8 h-8 grid place-items-center rounded-full border hover:bg-muted transition"
                >
                  <ChevronLeft size={15} />
                </button>
              )}
              <button
                type="button"
                onClick={next}
                className="flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3.5 py-1.5 text-sm font-semibold hover:brightness-110 transition"
              >
                {step === TOUR_STEPS.length - 1 ? t("tutorial.terminer") : t("tutorial.suivant")}{" "}
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tourPop { from { transform: scale(.92); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { .transition-all { transition: none !important } }
      `}</style>
    </div>
  );
}

function Blur({ style }) {
  return (
    <div
      className="fixed bg-black/45 backdrop-blur-sm transition-all duration-200"
      style={style}
    />
  );
}